import { useEffect, useMemo, useRef, useState } from "react";
import API_BASE from "../../config";
import s from "./ReportsPage.module.css";

/** Adds `.scrolling` while the user is actively scrolling; removes it ~700ms
 *  after the last event. Same pattern ActivityTab uses — duplicated rather
 *  than shared so this file stays self-contained. */
function useScrollActiveClass(activeClass) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let t;
    const onScroll = () => {
      el.classList.add(activeClass);
      clearTimeout(t);
      t = setTimeout(() => el.classList.remove(activeClass), 700);
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => { clearTimeout(t); el.removeEventListener("scroll", onScroll); };
  }, [activeClass]);
  return ref;
}

/** Formats a number as Philippine peso WITHOUT the symbol (the card prints
 *  the ₱ separately so colour can apply to the whole amount). */
function fmtMoney(n) {
  if (n == null || n === "") return "0.00";
  const v = Number(n);
  if (!Number.isFinite(v)) return "0.00";
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", year: "numeric",
    hour: "numeric", minute: "2-digit",
  });
}

const STATUS_FILTERS = [
  { value: "",         label: "All statuses" },
  { value: "PENDING",  label: "Pending"  },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
];

const SINCE_OPTIONS = [
  { value: "7",   label: "Last 7 days"  },
  { value: "30",  label: "Last 30 days" },
  { value: "90",  label: "Last 90 days" },
  { value: "all", label: "All time"     },
];

/** Cards collapse below the fold once the list crosses this many rows. The
 *  scroll wrapper kicks in instead of letting the page itself grow without
 *  bound. Pulled out as a constant so the threshold is easy to tune. */
const SCROLL_AFTER_ROWS = 15;

/** Status pill colours — same palette as the topbar inbox so the look is
 *  consistent between the live queue and the historical report. */
function statusStyle(status) {
  if (status === "APPROVED") return { bg: "#e3f2e6", fg: "#1f6a2d", border: "#bcdcc4" };
  if (status === "REJECTED") return { bg: "#fdecea", fg: "#c0392b", border: "#e6b8b4" };
  return                            { bg: "#fff8e1", fg: "#8a6a1c", border: "#efe1a8" };
}

/**
 * Reports → Deletion Requests
 *
 * Read-only history of every payment-deletion request, newest first. Each
 * row collapses to a one-line summary (amount + status + student + invoice
 * + who-did-what) and expands on click to show reasons and the full
 * payment / invoice detail blocks. Past ~15 rows the list itself becomes
 * scrollable so the rest of the Reports page stays in view.
 *
 * Data shape: GET /api/payment-deletion-requests returns the same enriched
 * objects the inbox uses; for APPROVED rows whose payment is gone, the
 * backend transparently returns snapshot data instead — see
 * PaymentDeletionRequestController.enrich().
 */
export default function DeletionRequestsTab() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState("");
  const [statusF, setStatusF] = useState("");
  const [sinceDays, setSince] = useState("30");
  const [search, setSearch]   = useState("");
  // One Set tracks which cards are currently expanded. Using a Set instead
  // of a per-card `useState` keeps state at the parent so filtering or
  // re-fetching doesn't reset every card's open/closed state independently.
  const [expanded, setExpanded] = useState(() => new Set());
  const scrollRef = useScrollActiveClass(s.scrolling);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr("");
    fetch(`${API_BASE}/api/payment-deletion-requests`, {
      headers: { "X-User-Role": "Admin" },
    })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`Load failed (${r.status})`)))
      .then(list => { if (!cancelled) setRows(Array.isArray(list) ? list : []); })
      .catch(e => !cancelled && setErr(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  // Filter (status + window + search) THEN sort by createdAt desc so the
  // newest request is always at the top, regardless of what the backend
  // happened to return. The repo's findAll() doesn't carry an ORDER BY.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const cutoff = sinceDays === "all"
      ? 0
      : Date.now() - Number(sinceDays) * 24 * 60 * 60 * 1000;
    const out = rows.filter(r => {
      if (statusF && r.status !== statusF) return false;
      if (cutoff && r.createdAt) {
        const t = new Date(r.createdAt).getTime();
        if (!Number.isNaN(t) && t < cutoff) return false;
      }
      if (!q) return true;
      const stu = r.student || {};
      const haystack = [
        r.requestedByUsername, r.reviewedByUsername,
        r.reason, r.decisionReason,
        stu.firstName, stu.lastName, stu.studentNumber,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(q);
    });
    out.sort((a, b) => {
      const ta = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const tb = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return tb - ta; // newest first
    });
    return out;
  }, [rows, statusF, sinceDays, search]);

  // Summary pills — counts by status across the filtered set so the totals
  // reflect what the user is actually looking at.
  const summary = useMemo(() => {
    let pending = 0, approved = 0, rejected = 0;
    for (const r of filtered) {
      if (r.status === "PENDING")  pending++;
      else if (r.status === "APPROVED") approved++;
      else if (r.status === "REJECTED") rejected++;
    }
    return { total: filtered.length, pending, approved, rejected };
  }, [filtered]);

  const toggle = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const expandAll   = () => setExpanded(new Set(filtered.map(r => r.id)));
  const collapseAll = () => setExpanded(new Set());

  // Past the threshold, wrap the list in a max-height scroll container so
  // the page itself doesn't grow into a 100-card wall. Below the threshold
  // we let it lay out naturally — no scrollbar, no fade.
  const scrollable = filtered.length > SCROLL_AFTER_ROWS;
  const listStyle  = scrollable
    ? { maxHeight: "65vh", overflowY: "auto", paddingRight: 4 }
    : null;
  const listClass = scrollable ? s.timelineScroll : "";

  return (
    <div className={s.panel}>
      {/* Filter bar — status / since / search */}
      <div className={s.filterRow}>
        <label className={s.filterField}>
          <span>Status</span>
          <select value={statusF} onChange={e => setStatusF(e.target.value)}>
            {STATUS_FILTERS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className={s.filterField}>
          <span>Since</span>
          <select value={sinceDays} onChange={e => setSince(e.target.value)}>
            {SINCE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        <label className={`${s.filterField} ${s.filterGrow}`}>
          <span>Search</span>
          <div className={s.searchWrap}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Student, requester, reviewer, reason…"
            />
            {search && (
              <button className={s.clearBtn} onClick={() => setSearch("")} aria-label="Clear">✕</button>
            )}
          </div>
        </label>
      </div>

      {/* Summary pills + expand/collapse-all controls */}
      <div className={s.summary} style={{ alignItems: "center" }}>
        <span className={s.summaryPill}>
          {summary.total} request{summary.total === 1 ? "" : "s"}
        </span>
        <span className={s.summaryPillMuted}>{summary.pending} pending</span>
        <span className={s.summaryPillMuted}>{summary.approved} approved</span>
        <span className={s.summaryPillMuted}>{summary.rejected} rejected</span>
        {filtered.length > 0 && (
          <span style={{ marginLeft: "auto", display: "inline-flex", gap: 6 }}>
            <button type="button" onClick={expandAll}
              style={miniBtnStyle}>Expand all</button>
            <button type="button" onClick={collapseAll}
              style={miniBtnStyle}>Collapse all</button>
          </span>
        )}
      </div>

      {err && <div className={s.error}>{err}</div>}

      {loading ? (
        <div className={s.empty}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div className={s.empty}>No deletion requests match the current filters.</div>
      ) : (
        <div
          ref={scrollable ? scrollRef : null}
          className={listClass}
          style={listStyle}>
          <div style={{ display: "grid", gap: 10 }}>
            {filtered.map(r => (
              <RequestCard
                key={r.id}
                req={r}
                expanded={expanded.has(r.id)}
                onToggle={() => toggle(r.id)}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const miniBtnStyle = {
  fontSize: "0.72rem", padding: "3px 8px", borderRadius: 6,
  background: "#fff", color: "#6b5a3a",
  border: "1px solid #d6cdb8", cursor: "pointer",
};

/** One row card. Collapsed by default — header is clickable to expand and
 *  reveal the reasons + the PAYMENT / INVOICE detail blocks. */
function RequestCard({ req, expanded, onToggle }) {
  const r   = req;
  const p   = r.payment || {};
  const stu = r.student || {};
  const inv = r.invoice || {};
  const pal = statusStyle(r.status);

  // "fromSnapshot" is stamped per nested object by the backend when the
  // live row is gone (typical APPROVED case). Surface a small "Historical"
  // hint so the admin knows the values are preserved-not-live.
  const usingSnapshot = p.fromSnapshot === true || stu.fromSnapshot === true || inv.fromSnapshot === true;

  const studentName = (stu.lastName || stu.firstName)
    ? `${stu.lastName || ""}, ${stu.firstName || ""}`.replace(/^, |, $/g, "")
    : "—";

  return (
    <div style={{
      background: "#faf6ee", borderRadius: 10,
      border: "1px solid #efe7d6",
      // Faint highlight when expanded so the user can see at a glance
      // which cards are open in a long list.
      boxShadow: expanded ? "inset 3px 0 0 #c9a227" : "none",
    }}>
      {/* Header: clickable bar that toggles expansion. Whole bar is the
          target, not just a tiny chevron, so it's hard to miss-click. */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        style={{
          all: "unset", display: "block", width: "100%", boxSizing: "border-box",
          padding: "10px 14px", cursor: "pointer", borderRadius: 10,
        }}>
        <div style={{ display: "flex", justifyContent: "space-between",
                      alignItems: "baseline", gap: 8, marginBottom: 6 }}>
          <span style={{ display: "inline-flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontWeight: 800, color: "#1f6a2d", fontSize: "1.05rem" }}>
              ₱{fmtMoney(p.amount)}
            </span>
            <span style={{
              fontSize: "0.66rem", fontWeight: 800,
              letterSpacing: 0.4, textTransform: "uppercase",
              padding: "2px 7px", borderRadius: 4,
              background: pal.bg, color: pal.fg,
              border: `1px solid ${pal.border}`,
            }}>
              {r.status}
            </span>
            {usingSnapshot && (
              <span title="Live payment/invoice no longer exists — showing the snapshot captured at request time."
                    style={{
                      fontSize: "0.62rem", fontWeight: 700, letterSpacing: 0.3,
                      textTransform: "uppercase", padding: "1px 6px", borderRadius: 4,
                      background: "#eee6d4", color: "#6b5a3a",
                      border: "1px solid #d6cdb8",
                    }}>
                Historical
              </span>
            )}
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <span style={{ fontSize: "0.74rem", color: "#8a8070" }}>
              {fmtDate(r.createdAt)}
            </span>
            {/* Chevron — visual cue for "this expands". Rotates when open. */}
            <span aria-hidden="true" style={{
              display: "inline-block", width: 10, height: 10,
              borderRight: "2px solid #8a8070",
              borderBottom: "2px solid #8a8070",
              transform: expanded ? "rotate(-135deg) translate(-2px,-2px)" : "rotate(45deg)",
              transition: "transform 0.18s ease",
            }} />
          </span>
        </div>

        {/* Method + student + invoice line */}
        <div style={{ fontSize: "0.82rem", color: "#3d342a", marginBottom: 4 }}>
          <span style={{
            display: "inline-block", padding: "2px 7px",
            fontSize: "0.66rem", fontWeight: 800, letterSpacing: 0.4,
            textTransform: "uppercase",
            background: "#e8dfc9", color: "#5a4e3a",
            borderRadius: 4, marginRight: 6,
          }}>
            {p.method || "—"}
          </span>
          {studentName}
          {stu.studentNumber ? <span style={{ color: "#8a8070" }}> · #{stu.studentNumber}</span> : null}
          {inv.id ? <span style={{ color: "#8a8070" }}> · invoice #{inv.id}</span> : null}
        </div>

        {/* Requester + reviewer trail — kept on the collapsed view because
            it's the primary "who did what" the admin scans for. */}
        <div style={{ fontSize: "0.78rem", color: "#6b5a3a" }}>
          Requested by <b>{r.requestedByUsername || "—"}</b>
          {r.requestedByRole ? ` · ${r.requestedByRole}` : ""}
          {r.status !== "PENDING" && (
            <>
              <span style={{ color: "#cfc7b6" }}> → </span>
              {r.status === "APPROVED" ? "Approved" : "Rejected"} by{" "}
              <b>{r.reviewedByUsername || "admin"}</b>
              {r.reviewedAt ? ` · ${fmtDate(r.reviewedAt)}` : ""}
            </>
          )}
        </div>
      </button>

      {/* Expanded body — reasons + per-card details. Hidden by default to
          keep the list compact when scanning many rows. */}
      {expanded && (
        <div style={{ padding: "0 14px 12px 14px",
                      borderTop: "1px solid #efe7d6", marginTop: 2 }}>
          {r.reason && (
            <div style={{ fontSize: "0.82rem", color: "#3d342a",
                          background: "#fff", padding: "6px 9px",
                          borderRadius: 6, marginTop: 10, marginBottom: 6,
                          borderLeft: "3px solid #c9a227" }}>
              <span style={{ fontSize: "0.7rem", fontWeight: 700,
                             color: "#8a8070", display: "block", marginBottom: 2 }}>
                Requester's reason
              </span>
              {r.reason}
            </div>
          )}
          {r.status === "REJECTED" && r.decisionReason && (
            <div style={{ fontSize: "0.82rem", color: "#3d342a",
                          background: "#fdecea", padding: "6px 9px",
                          borderRadius: 6, marginBottom: 6,
                          borderLeft: "3px solid #c0392b" }}>
              <span style={{ fontSize: "0.7rem", fontWeight: 700,
                             color: "#a04030", display: "block", marginBottom: 2 }}>
                Admin's reason for rejecting
              </span>
              {r.decisionReason}
            </div>
          )}
          {r.status === "APPROVED" && r.decisionReason && (
            <div style={{ fontSize: "0.82rem", color: "#3d342a",
                          background: "#e3f2e6", padding: "6px 9px",
                          borderRadius: 6, marginBottom: 6,
                          borderLeft: "3px solid #1f6a2d" }}>
              <span style={{ fontSize: "0.7rem", fontWeight: 700,
                             color: "#1f6a2d", display: "block", marginBottom: 2 }}>
                Admin's note on approval
              </span>
              {r.decisionReason}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                        gap: 8, fontSize: "0.78rem", color: "#3d342a", marginTop: 6 }}>
            <DetailBlock title="PAYMENT" rows={[
              ["Reference #", p.referenceNo || "—"],
              ["Bank",        p.bankName    || "—"],
              ["Paid at",     fmtDate(p.paidAt)],
              ["Recorded by", p.recordedByUsername || "—"],
              ["Particular",  p.particular  || "—"],
            ]} />
            <DetailBlock title="INVOICE" rows={[
              ["Invoice #", inv.id ?? "—"],
              ["Amount",    inv.amount != null ? `₱${fmtMoney(inv.amount)}` : "—"],
              ["Status",    inv.status || "—"],
              ["Due date",  inv.dueDate ? fmtDate(inv.dueDate) : "—"],
            ]} />
          </div>
        </div>
      )}
    </div>
  );
}

/** Compact label/value list used by the per-card details grid. */
function DetailBlock({ title, rows }) {
  return (
    <div style={{ background: "#fff", border: "1px solid #efe7d6",
                  borderRadius: 8, padding: "8px 10px" }}>
      <div style={{ fontSize: "0.66rem", fontWeight: 800,
                    letterSpacing: 0.5, color: "#8a8070", marginBottom: 4 }}>
        {title}
      </div>
      {rows.map(([label, value]) => (
        <div key={label} style={{ display: "flex", justifyContent: "space-between",
                                  gap: 8, padding: "1px 0" }}>
          <span style={{ color: "#8a8070" }}>{label}</span>
          <span style={{ color: "#3d342a", textAlign: "right" }}>{value ?? "—"}</span>
        </div>
      ))}
    </div>
  );
}
